import React from 'react';
import { motion } from 'framer-motion';

interface VirtualAccountApiDisplayProps {
  apiRequest?: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body: Record<string, any>;
  };
  apiResponse?: {
    currency: string;
    payment_rails: string[];
    iban: string;
    bic: string;
    account_holder_name: string;
    bank_name: string;
    bank_address: string;
    destination: {
      currency: string;
      payment_rail: string;
      address: string;
    };
  };
  activeTab: 'request' | 'response';
}

export const VirtualAccountApiDisplay: React.FC<VirtualAccountApiDisplayProps> = ({ 
  apiRequest, 
  apiResponse, 
  activeTab 
}) => {
  const formatJsonWithHighlighting = (obj: any, indent = 0): JSX.Element => {
    if (typeof obj === 'string') {
      return <span className="text-green-600">"{obj}"</span>;
    }
    if (typeof obj === 'number') {
      return <span className="text-green-600">{obj.toString()}</span>;
    }
    if (typeof obj === 'boolean') {
      return <span className="text-green-600">{obj.toString()}</span>;
    }
    if (obj === null) {
      return <span className="text-green-600">null</span>;
    }
    
    if (Array.isArray(obj)) {
      return (
        <span>
          {'[\n'}
          {obj.map((item, index) => (
            <span key={index}>
              {'  '.repeat(indent + 1)}
              {formatJsonWithHighlighting(item, indent + 1)}
              {index < obj.length - 1 ? ',' : ''}
              {'\n'}
            </span>
          ))}
          {'  '.repeat(indent)}{']'}
        </span>
      );
    }
    
    if (typeof obj === 'object') {
      const entries = Object.entries(obj);
      return (
        <span>
          {'{\n'}
          {entries.map(([key, value], index) => (
            <span key={key}>
              {'  '.repeat(indent + 1)}
              <span className="text-blue-600">"{key}"</span>
              <span className="text-gray-600">: </span>
              {formatJsonWithHighlighting(value, indent + 1)}
              {index < entries.length - 1 ? ',' : ''}
              {'\n'}
            </span>
          ))}
          {'  '.repeat(indent)}{'}'}
        </span>
      );
    }
    
    return <span className="text-green-600">{String(obj)}</span>;
  };

  if (activeTab === 'request' && apiRequest) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="code-block"
      >
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <span className="text-blue-600 font-semibold">curl</span>
            <span className="text-gray-600">--request</span>
            <span className="text-green-600 font-semibold">{apiRequest.method}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-gray-600">--url</span>
            <span className="text-blue-600">https://api.across.money{apiRequest.url}</span>
          </div>
          
          {Object.entries(apiRequest.headers).map(([key, value]) => (
            <div key={key} className="flex items-center space-x-2">
              <span className="text-gray-600">--header</span>
              <span className="text-green-600">'{key}: {value}'</span>
            </div>
          ))}
          
          <div className="flex items-center space-x-2">
            <span className="text-gray-600">--data</span>
          </div>
          
          <div className="ml-4 text-sm">
            <pre className="whitespace-pre-wrap">
              {formatJsonWithHighlighting(apiRequest.body)}
            </pre>
          </div>
        </div>
      </motion.div>
    );
  }

  if (activeTab === 'response' && apiResponse) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="code-block"
      >
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <span className="text-black-600">{`{`}</span>
          </div>
          
          <div className="ml-4 space-y-1">
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">"currency"</span>
              <span className="text-gray-600">:</span>
              <span className="text-green-600">"{apiResponse.currency}"</span>
              <span className="text-gray-600">,</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">"payment_rails"</span>
              <span className="text-gray-600">:</span>
              <span className="text-green-600">[{apiResponse.payment_rails.map(rail => `"${rail}"`).join(', ')}]</span>
              <span className="text-gray-600">,</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">"iban"</span>
              <span className="text-gray-600">:</span>
              <span className="text-green-600">"{apiResponse.iban}"</span>
              <span className="text-gray-600">,</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">"bic"</span>
              <span className="text-gray-600">:</span>
              <span className="text-green-600">"{apiResponse.bic}"</span>
              <span className="text-gray-600">,</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">"account_holder_name"</span>
              <span className="text-gray-600">:</span>
              <span className="text-green-600">"{apiResponse.account_holder_name}"</span>
              <span className="text-gray-600">,</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">"bank_name"</span>
              <span className="text-gray-600">:</span>
              <span className="text-green-600">"{apiResponse.bank_name}"</span>
              <span className="text-gray-600">,</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">"bank_address"</span>
              <span className="text-gray-600">:</span>
              <span className="text-green-600">"{apiResponse.bank_address}"</span>
              <span className="text-gray-600">,</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">"destination"</span>
              <span className="text-gray-600">:</span>
              <span className="text-black-600">{`{`}</span>
            </div>
            
            <div className="ml-8 space-y-1">
              <div className="flex items-center space-x-2">
                <span className="text-blue-600">"currency"</span>
                <span className="text-gray-600">:</span>
                <span className="text-green-600">"{apiResponse.destination.currency}"</span>
                <span className="text-gray-600">,</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-blue-600">"payment_rail"</span>
                <span className="text-gray-600">:</span>
                <span className="text-green-600">"{apiResponse.destination.payment_rail}"</span>
                <span className="text-gray-600">,</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-blue-600">"address"</span>
                <span className="text-gray-600">:</span>
                <span className="text-green-600">"{apiResponse.destination.address}"</span>
              </div>
            </div>
            
            <div className="ml-4">
              <span className="text-black-600">{`}`}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-black-600">{`}`}</span>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
};
